import { Injectable, Injector } from '@angular/core';
import { Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { GameModel } from '../models/game.model';
import { ThemeService } from '../../shared/theme/services/theme.service';
import { RequiredGameActionModel } from '../models/required-game-action.model';
import { AvatarBuilderResponseModel } from '../../avatar/models/avatar-builder-response.model';

const API_ENDPOINT = '/api/game/';

@Injectable({
  providedIn: 'root',
})
export class GameService {

  public currentGameSubject: Subject<GameModel>;
  public game: GameModel = new GameModel();
  public requiredGameActions: RequiredGameActionModel[] = [];
  public requiredGameActionsSubject: Subject<RequiredGameActionModel[]> = new Subject<RequiredGameActionModel[]>();
  public noRequiredGameActionsSubject: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  public hasRequiredActions: boolean = true;

  private cachedGameModules: Observable<string[]>;
  private cachedGameRequest: Observable<GameModel>;

  /**
   * Constructor
   *
   * @param {HttpClient} http
   * @param {ThemeService} themeService
   * @param {Injector} injector
   */
  public constructor(
    private http: HttpClient,
    private themeService: ThemeService,
    private injector: Injector,
  ) {
    this.currentGameSubject = new Subject<GameModel>();
  }

  /**
   * Loads the game for the app, only after being logged in.
   *
   * @return {Observable<GameModel>}
   */
  public loadCurrentGame(reload: boolean = false): Observable<GameModel> {
    if (reload || !this.cachedGameRequest) {
      this.cachedGameRequest = this.http.get(API_ENDPOINT + 'get/current')
      .pipe(
        map((response: { items: { game: GameModel, webAccessibility: boolean } }) => {

          this.game = response.items.game;
          if (this.game == null) {
            throwError('No game loaded!');
          }

          this.themeService.setTheme(response.items.game.theme);
          if (response.items.webAccessibility) {
            this.themeService.webAccessibilitySubject.next(true);
          }

          return response.items.game;
        }),
        shareReplay(1),
      );
    }

    return this.cachedGameRequest;
  }

  /**
   * Send a signal that the player leaves the game
   */
  public leaveCurrentGame(): Observable<boolean> {
    return this.http.get(API_ENDPOINT + 'post/leave').pipe(
      map((response: {message: string}) => {
        console.log('leave game');
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  /**
   * Loads the required routes for the app, only after being logged in.
   *
   * @return Observable<any>
   */
  public loadRequiredRoutes(): Observable<any> {
    return this.http.get(API_ENDPOINT + 'get/required-routes')
    .pipe(
      map((response: { error: string, items: { actions: RequiredGameActionModel[] } }) => {
        this.requiredGameActions = response.items.actions;
        if (this.requiredGameActions.length === 0) {
          this.hasRequiredActions = false;
        }
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  /**
   * Observable for the collection of RequiredGameActions is updated
   *
   * @return {Observable<RequiredGameActionModel[]>}
   */
  public getRequiredGameActions(): Observable<RequiredGameActionModel[]> {
    return this.requiredGameActionsSubject.asObservable();
  }

  /**
   * Observable for when there are no more RequiredGameActions
   *
   * @return {Observable<boolean>}
   */
  public hasNoRequiredGameActions(): Observable<boolean> {
    return this.noRequiredGameActionsSubject.asObservable();
  }

  /**
   * Tell the App that there are no more RequiredGameActions
   *
   * @return void
   */
  public setNoMoreRequiredGameActions(): void {
    this.noRequiredGameActionsSubject.next(true);
  }

  /**
   * Removes the passed game action from the requiredGameActions array
   *
   * @param {RequiredGameActionModel} requiredGameAction
   *
   * @return void
   */
  public unSetRequiredGameAction(requiredGameAction: RequiredGameActionModel): void {
    this.requiredGameActions = this.requiredGameActions.filter(obj => obj !== requiredGameAction);
  }

  /**
   * Getting the game modules for the current game
   *
   * @return {Observable<string[]>}
   */
  public getGameModules(): Observable<string[]> {
    if (!this.cachedGameModules) {
      this.cachedGameModules = this.http.get(API_ENDPOINT + 'get/modules').pipe(
        map((response: { items: { modules: string[] } }) => {
          return response.items.modules;
        }),
        shareReplay(1),
        catchError((error: HttpErrorResponse) => {
          return throwError(error.error);
        }),
      );
    }

    return this.cachedGameModules;
  }
}
